import { Suspense, useEffect, useMemo, useState } from "react";
import { OccupancyLayer, type OccupancyCell } from "./OccupancyLayer";
import { PalletLoadLayer } from "./PalletLoadLayer";
import { getInventoryDivergences, getInventoryOccupancy, getInventoryProducts, listInventoryRuns, selectedPaintableRuns, type ProductCatalogRow } from "../api/rest";
import { catalogDescriptions, matchProducts } from "./productSearch";
import { agingColorForFifo, agingCounts, agingDeckPaint, expiryColorForDate, expiryCounts, expiryDeckPaint } from "./expiryPaint";
import { wmsBoxPaint, wmsClassByAddress, wmsCounts, wmsDeckPaint, type WmsClassIndex, type WmsClassKey } from "./wmsPaint";
import { useUIStore } from "../stores/uiStore";
import { useTelemetryStore } from "../stores/telemetryStore";
import { useDebouncedValue } from "../hooks/useDebouncedValue";

interface Props {
  siteId: string;
  warehouseId: string;
}

/**
 * ADR 212: the scene rebuild a changed search triggers costs ~120 ms at JSL
 * scale (15k bins) — the matched/ghost split remounts every instanced mesh, and
 * three.js re-derives a program for each. Typing paid that PER KEYSTROKE, which
 * is the stutter operators reported. Debounced, a six-character query rebuilds
 * once, after typing stops. Under a keystroke interval, so it never delays a
 * deliberate pause.
 */
const SEARCH_DEBOUNCE_MS = 180;

/** Shared empty index — a fresh Map each render would rebuild every classifier. */
const EMPTY_WMS_INDEX: WmsClassIndex = new Map();

/**
 * Data-fetching wrapper around the presentational `OccupancyLayer` (S4.1; re-scoped
 * by ADR 190): resolves the ACTIVE WAREHOUSE's latest paintable inventory run and
 * fetches its per-location occupancy, mounting the layer once there is something to
 * draw. Mounted/unmounted by `Scene.tsx` from `layers.occupancy`, keyed on
 * `${siteId}:${warehouseId}` — a warehouse switch forces a fresh instance, so one
 * warehouse's bins never render inside another's building.
 *
 * Run choice: `latestPaintableRun` (the ADR 190 rule, shared with the KPI-badge
 * layer). The old `mapName` gate is gone: occupancy geometry resolves through the
 * warehouse since ADR 190, so a mapless run paints fine.
 *
 * The painted occupancy is still batch: iC3 runs arrive as imports (ADR 99), so one
 * fetch per mount/`sceneDataVersion` bump is enough and there is no polling. The version
 * dep is the height-edit write path (bin inspector PATCH → bumpSceneData → refetch).
 * What IS live is coverage — `visitedAddresses` (ADR 324) marks the addresses an
 * in-progress run has reached, streamed over the WS. It never changes what a bin is
 * painted as, only whether the kit has been there yet.
 *
 * A selected bin belongs to this layer's data: tear the selection down with the
 * layer (toggle off / warehouse switch), or the inspector would keep showing a bin
 * that is no longer on screen.
 */
export function OccupancyLayerContainer({ siteId, warehouseId }: Props) {
  const [locations, setLocations] = useState<OccupancyCell[] | null>(null);
  const [catalog, setCatalog] = useState<ProductCatalogRow[]>([]);
  const dataVersion = useUIStore((s) => s.sceneDataVersion);
  const setSelectedBin = useUIStore((s) => s.setSelectedBin);
  const productQuery = useUIStore((s) => s.productQuery);
  const productFilters = useUIStore((s) => s.productFilters);
  const setProductMatches = useUIStore((s) => s.setProductMatches);
  const runSelection = useUIStore((s) => s.runSelection);
  // ADR 324 — Mode I1. Only this warehouse's stream, and only while the operator wants it:
  // a run sweeping the next building over must not repaint the one on screen.
  const liveInventoryOn = useUIStore((s) => s.layers.liveInventory);
  const activity = useTelemetryStore((s) => s.inventoryActivity);
  const visited = useMemo(() => {
    if (!liveInventoryOn || !activity) return null;
    if (activity.siteId !== siteId) return null;
    // A run that never named its warehouse still belongs to the site; painting it on the
    // active warehouse is the best available reading and the address join filters the rest.
    if (activity.warehouseId && activity.warehouseId !== warehouseId) return null;
    return new Set(activity.visited.keys());
  }, [liveInventoryOn, activity, siteId, warehouseId]);
  // Dwell anchor: one date per mount is plenty — runs are batch imports, and a
  // midnight rollover mid-session is corrected by any remount/refetch.
  const [todayIso] = useState(() => new Date().toISOString().slice(0, 10));

  useEffect(() => {
    let cancelled = false;
    listInventoryRuns(siteId)
      .then((runs) => {
        // ADR 198: the LayerPanel run picker chooses which runs paint; the
        // default stays the ADR 190 latest-only rule. Several runs merge
        // server-side, newest scan per address winning.
        const selected = selectedPaintableRuns(runs, warehouseId, runSelection);
        return selected.length > 0
          ? getInventoryOccupancy(siteId, selected[0].id, selected.map((r) => r.id))
          : null;
      })
      .then((occ) => {
        if (cancelled) return;
        setLocations(occ?.mapResolved ? occ.locations : null);
      })
      .catch(() => { if (!cancelled) setLocations(null); });
    return () => { cancelled = true; };
  }, [siteId, warehouseId, dataVersion, runSelection]);

  // ADR 196: the WMS product catalog feeds description search. Failure (or no
  // snapshot) degrades to code-only matching — never an error, never a retry loop.
  useEffect(() => {
    let cancelled = false;
    getInventoryProducts(siteId, warehouseId)
      .then((rows) => { if (!cancelled) setCatalog(rows); })
      .catch(() => { if (!cancelled) setCatalog([]); });
    return () => { cancelled = true; };
  }, [siteId, warehouseId]);

  // One debounced tuple, not two: query and filters landing on separate timers
  // would rebuild the scene twice for a single edit.
  const typed = useMemo(
    () => ({ query: productQuery, filters: productFilters }),
    [productQuery, productFilters],
  );
  const search = useDebouncedValue(typed, SEARCH_DEBOUNCE_MS);

  // Built once per catalog (~52k rows at JSL) rather than inside every match.
  const catalogIndex = useMemo(() => catalogDescriptions(catalog), [catalog]);

  // Null while the search is inactive — the layers then render normally.
  const matched = useMemo(
    () => matchProducts(locations ?? [], catalogIndex, search.query, search.filters, todayIso),
    [locations, catalogIndex, search, todayIso],
  );

  // The search box (DOM, outside the canvas) shows the hit count and the
  // relocation simulator prices the matched set (ADR 351); this container owns
  // the cells, so it owns both. Cleared on unmount — a stale selection must
  // not survive a layer toggle or warehouse switch.
  useEffect(() => {
    setProductMatches(matched ? [...matched] : null);
  }, [matched, setProductMatches]);
  useEffect(() => () => setProductMatches(null), [setProductMatches]);

  useEffect(() => () => setSelectedBin(null), [setSelectedBin]);

  // ADR 354 — Pintar por Validade. The classifiers are built here (the cells'
  // owner holds the clock) and handed down; the legend tallies go to the store
  // the same way productMatches does, cleared with the layer so the LayerPanel
  // never shows counts for a scene that is not on screen.
  // ADR 198 movement focus: the origin is only marked while the search still
  // isolates the product it was picked for (any other query drops it).
  const movementFocus = useUIStore((s) => s.movementFocus);
  const origin = useMemo(
    () => (movementFocus && movementFocus.product === productQuery ? new Set([movementFocus.fromAddress]) : null),
    [movementFocus, productQuery],
  );
  const decks = useUIStore((s) => s.layers.decks);
  const paintBy = useUIStore((s) => s.paintBy);
  const setPaintCounts = useUIStore((s) => s.setPaintCounts);
  // ADR 375: the WMS comparison of the runs that actually own painted cells —
  // fetched only while the mode is on (the rows are the report's full payload,
  // 15k+ at JSL, and no other mode needs them). Imperative like the occupancy
  // fetch above, so this layer keeps one fetching style and no query client.
  const paintedRunIds = useMemo(() => {
    const ids = new Set<string>();
    for (const cell of locations ?? []) if (cell.runId) ids.add(cell.runId);
    return [...ids].sort();
  }, [locations]);
  // The fetched index carries the run set it was built FOR, and render uses it
  // only while that set is still the one on screen (ADR 136: guard during
  // render, never clear with a setState in the effect body). A run switch
  // therefore mutes instantly instead of painting the previous run's verdicts.
  const runKey = paintedRunIds.join(",");
  const [wms, setWms] = useState<{ runKey: string; index: WmsClassIndex } | null>(null);
  const wmsIndex = paintBy === "wms" && wms?.runKey === runKey ? wms.index : EMPTY_WMS_INDEX;
  useEffect(() => {
    if (paintBy !== "wms" || paintedRunIds.length === 0) return;
    let cancelled = false;
    void Promise.all(paintedRunIds.map((id) =>
      getInventoryDivergences(siteId, id)
        .then((d) => [id, wmsClassByAddress(d.rows)] as const)
        // A run with no WMS snapshot answers empty; a failed fetch must leave
        // the floor grey (no verdict), never painted from a stale index.
        .catch(() => [id, new Map<string, WmsClassKey>()] as const)))
      .then((entries) => { if (!cancelled) setWms({ runKey, index: new Map(entries) }); });
    return () => { cancelled = true; };
  }, [siteId, paintBy, paintedRunIds, runKey]);

  const deckPaint = useMemo(
    () => (paintBy === "expiry" ? expiryDeckPaint(todayIso)
         : paintBy === "aging" ? agingDeckPaint(todayIso)
         : paintBy === "wms" ? wmsDeckPaint(wmsIndex)
         : null),
    [paintBy, todayIso, wmsIndex],
  );
  const boxPaint = useMemo(
    () => (paintBy === "expiry"
      ? (b: { expiryDate: string | null }) => expiryColorForDate(b.expiryDate, todayIso)
      : paintBy === "aging"
        ? (b: { fifoDate: string | null }) => agingColorForFifo(b.fifoDate, todayIso)
        : paintBy === "wms" ? wmsBoxPaint(wmsIndex)
          : null),
    [paintBy, todayIso, wmsIndex],
  );
  const counts = useMemo(
    () => (locations && locations.length > 0
      ? (paintBy === "aging" ? agingCounts(locations, todayIso)
       : paintBy === "wms" ? wmsCounts(locations, wmsIndex)
       : expiryCounts(locations, todayIso))
      : null),
    [locations, paintBy, todayIso, wmsIndex],
  );
  useEffect(() => { setPaintCounts(counts); }, [counts, setPaintCounts]);
  useEffect(() => () => setPaintCounts(null), [setPaintCounts]);

  if (!locations || locations.length === 0) return null;
  return (
    <>
      {/* The floor squares are their own reference layer (Camadas → Referência):
          an operator reading pallets can drop the tint without losing them. */}
      {decks && (
        <OccupancyLayer locations={locations} matchedAddresses={matched} visitedAddresses={visited}
                        originAddresses={origin} paint={deckPaint} />
      )}
      {/* Own boundary: the GLB parts suspend on first load, and that must not
          blank the sibling tints (Scene's boundary wraps the whole canvas). */}
      <Suspense fallback={null}>
        <PalletLoadLayer locations={locations} matchedAddresses={matched} boxPaint={boxPaint} />
      </Suspense>
    </>
  );
}
